<template>
  <div v-if="isOpen" class="modal-backdrop-custom" @click.self="close">
    <div class="modal-box" style="max-width: 900px; max-height: 90vh; overflow-y: auto;">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="fw-bold mb-0">증빙서류 조회</h6>
        <button type="button" class="btn-close" aria-label="Close" @click="close"></button>
      </div>

      <!-- 파일 목록 -->
      <div v-if="files.length === 0" class="text-center text-muted py-4">
        파일이 없습니다.
      </div>

      <template v-else>
        <!-- 현재 파일 정보 -->
        <div class="bg-light p-3 rounded mb-3" style="font-size: 0.9rem;">
          <div class="row g-2">
            <div class="col-6">
              <strong>파일명:</strong> {{ currentFile.OriginalFileName }}
            </div>
            <div class="col-6">
              <strong>크기:</strong> {{ formatBytes(currentFile.FileSize) }}
            </div>
            <div class="col-6">
              <strong>업로드일:</strong> {{ formatDate(currentFile.CreatedDate) }}
            </div>
            <div class="col-6">
              <strong>파일 정보:</strong> {{ currentIndex + 1 }} / {{ files.length }}
            </div>
          </div>
        </div>

        <!-- 이미지 뷰어 -->
        <div v-if="isImage(currentFile)" class="text-center mb-3">
          <img 
            :src="currentFile.url" 
            :alt="currentFile.OriginalFileName"
            style="max-width: 100%; max-height: 500px; border-radius: 8px; border: 1px solid #ddd;"
          />
        </div>

        <!-- 비이미지 파일 -->
        <div v-else class="alert alert-info mb-3" style="font-size: 0.9rem;">
          <strong>📄 {{ currentFile.OriginalFileName }}</strong><br/>
          <span class="text-muted">이미지가 아닌 파일입니다. 아래 버튼으로 다운로드 하세요.</span>
        </div>

        <!-- 네비게이션 및 액션 -->
        <div class="d-flex justify-content-between align-items-center gap-2">
          <!-- 이전/다음 네비게이션 -->
          <div class="d-flex gap-2">
            <button 
              class="btn btn-sm btn-outline-secondary" 
              @click="prevFile"
              :disabled="currentIndex === 0"
              title="이전 파일"
            >
              ◀ 이전
            </button>
            <button 
              class="btn btn-sm btn-outline-secondary" 
              @click="nextFile"
              :disabled="currentIndex === files.length - 1"
              title="다음 파일"
            >
              다음 ▶
            </button>
          </div>

          <!-- 다운로드 및 닫기 -->
          <div class="d-flex gap-2">
            <a 
              :href="currentFile.url" 
              download
              class="btn btn-sm btn-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              ⬇ 다운로드
            </a>
            <button 
              class="btn btn-sm btn-success"
              :disabled="!waitingSeq || updatingStatus"
              @click="markEvidenceReady"
            >
              ✅ 증빙 완료
            </button>
            <button 
              class="btn btn-sm btn-secondary" 
              @click="close"
            >
              닫기
            </button>
          </div>
        </div>

        <div v-if="statusMessage" class="mt-3">
          <div class="alert alert-info py-2 mb-0 small">{{ statusMessage }}</div>
        </div>

        <!-- 파일 목록 -->
        <div class="mt-4 pt-3 border-top">
          <h6 class="fw-bold mb-2">전체 파일 목록</h6>
          <div class="list-group">
            <button 
              v-for="(f, idx) in files"
              :key="f.FileSeq"
              type="button"
              class="list-group-item list-group-item-action text-start"
              :class="{ active: idx === currentIndex }"
              @click="goToFile(idx)"
              style="font-size: 0.9rem;"
            >
              <div class="d-flex justify-content-between align-items-center">
                <span>
                  <strong v-if="isImage(f)" class="text-info">🖼</strong>
                  <strong v-else class="text-muted">📄</strong>
                  {{ f.OriginalFileName }}
                </span>
                <span class="badge bg-secondary">{{ formatBytes(f.FileSize) }}</span>
              </div>
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FileViewerModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    waitingSeq: {
      type: Number,
      default: null
    },
    files: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentIndex: 0,
      updatingStatus: false,
      statusMessage: ''
    }
  },
  computed: {
    currentFile() {
      return this.files[this.currentIndex] || {}
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.currentIndex = 0
      }
    }
  },
  methods: {
    close() {
      this.$emit('close')
      this.statusMessage = ''
    },
    async markEvidenceReady() {
      if (!this.waitingSeq) return
      this.updatingStatus = true
      this.statusMessage = ''
      try {
        const response = await fetch(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/${this.waitingSeq}/status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: 'WAITING' })
        })
        const result = await response.json()
        if (!response.ok) {
          throw new Error(result.message || '상태 변경에 실패했습니다.')
        }
        this.statusMessage = '증빙 완료 처리되었습니다.'
        this.$emit('status-updated')
      } catch (e) {
        this.statusMessage = e.message || '증빙 완료 처리 중 오류가 발생했습니다.'
      } finally {
        this.updatingStatus = false
      }
    },
    isImage(file) {
      if (!file.mimeType) return false
      return file.mimeType.startsWith('image/')
    },
    nextFile() {
      if (this.currentIndex < this.files.length - 1) {
        this.currentIndex++
      }
    },
    prevFile() {
      if (this.currentIndex > 0) {
        this.currentIndex--
      }
    },
    goToFile(idx) {
      this.currentIndex = idx
    },
    formatBytes(bytes) {
      if (!bytes) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      let i = 0
      let v = bytes
      while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
      return v.toFixed((i === 0) ? 0 : 2) + ' ' + units[i]
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      try {
        const d = new Date(dateStr)
        return d.toLocaleDateString('ko-KR') + ' ' + d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })
      } catch {
        return dateStr
      }
    }
  }
}
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-box {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
  min-width: 300px;
}

.list-group-item.active {
  background-color: #0d6efd;
  border-color: #0d6efd;
}
</style>
